import { DEFAULT_MARKET, MARKETS_LIST, ZERO_WEI } from '@kwenta/sdk/constants'
import { PerpsProvider } from '@kwenta/sdk/types'
import { formatDollars, formatPercent } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import { DialogOverlay } from '@reach/dialog'

import { type FC, memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import QuestionMarkIcon from 'assets/svg/app/question-mark.svg'
import SettingsIcon from 'assets/svg/app/settings.svg'
import ColoredPrice from 'components/ColoredPrice'
import RoundedSelect from 'components/RoundedSelect'
import { Body } from 'components/Text'
import Tooltip from 'components/Tooltip/Tooltip'
import { FlexDivCol, FlexDivRow, FlexDivRowCentered } from 'components/layout/flex'
import { BANNER_HEIGHT_DESKTOP, BANNER_HEIGHT_MOBILE } from 'constants/announcement'
import { FUNDING_RATE_PERIODS } from 'constants/funding'
import { NO_VALUE } from 'constants/placeholder'
import useClickOutside from 'hooks/useClickOutside'
import useWindowSize from 'hooks/useWindowSize'
import { setOpenModal } from 'state/app/reducer'
import { selectShowBanner } from 'state/app/selectors'
import {
	selectMarketAsset,
	selectMarketInfo,
	selectMarketPriceInfo,
	selectOpenMarketDropdownList,
	selectPerpsProvider,
	selectSkewAdjustedPriceInfo,
} from 'state/futures/common/selectors'
import { setPreferences } from 'state/futures/reducer'
import { selectPerpsInterestRate, selectSelectedInputHours } from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { select24hrPriceChanges, selectOffchainPricesInfo } from 'state/prices/selectors'
import media from 'styles/media'

import MarketsDropdownHeader from '../Trade/MarketsDropdownHeader'
import MarketsDropdownList from '../Trade/MarketsDropdownList'
import { MARKETS_DETAILS_HEIGHT_DESKTOP } from '../styles'

import Button from 'components/Button'
import { DRAG_HANDLE_CLASS_NAME } from 'components/GridLayout'
import MarketDetail, { MarketDetailValue } from './MarketDetail'
import { MarketDataKey } from './utils'

type MarketDetailsProps = {
	mobile?: boolean
}

interface OpenInterestDetailProps extends MarketDetailsProps {
	isLong?: boolean
}

const MarketDetails: FC<MarketDetailsProps> = memo(() => {
	const dispatch = useAppDispatch()
	const { deviceType } = useWindowSize()
	const mobileOrTablet = deviceType !== 'desktop'
	const openMarketDropdownList = useAppSelector(selectOpenMarketDropdownList)
	const { ref } = useClickOutside(() => dispatch(setOpenModal(null)))
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const showBanner = useAppSelector(selectShowBanner)

	const onOpenMarketDropdownList = useCallback(
		() =>
			openMarketDropdownList
				? dispatch(setOpenModal(null))
				: dispatch(setOpenModal('manage_markets')),
		[dispatch, openMarketDropdownList]
	)

	const SelectedMarketDetailsView = mobileOrTablet ? (
		<MarketDetailsContainer mobile={mobileOrTablet} perpsProvider={perpsProvider}>
			<MainMarketDetailsContainer>
				<FullWidthFlexDivRowCentered>
					<FlexDivRowCentered>
						<MarketsDropdownHeader onClick={onOpenMarketDropdownList} />
						<MarketPriceDetail />
					</FlexDivRowCentered>
					<MenuButton onClick={() => dispatch(setOpenModal('preferences'))} noOutline>
						<SettingsIcon width={17} height={18} />
					</MenuButton>
				</FullWidthFlexDivRowCentered>
			</MainMarketDetailsContainer>
			{openMarketDropdownList && (
				<StyledDialogOverlay>
					<DropdownContainer deviceType={deviceType} showBanner={showBanner} ref={ref}>
						<MarketsDropdownList />
					</DropdownContainer>
				</StyledDialogOverlay>
			)}
			<OtherMarketDetailsContainer>
				<IndexPriceDetail mobile={mobileOrTablet} />
				<DailyChangeDetail mobile={mobileOrTablet} />
				<OpenInterestDetail isLong />
				<OpenInterestDetail />
				<MarketSkew mobile={mobileOrTablet} />
				<HourlyFundingDetail mobile={mobileOrTablet} />
			</OtherMarketDetailsContainer>
		</MarketDetailsContainer>
	) : (
		<MarketDetailsContainer className={DRAG_HANDLE_CLASS_NAME} perpsProvider={perpsProvider}>
			<MarketsDropdownHeader onClick={onOpenMarketDropdownList} />
			<MarketPriceDetail />
			<IndexPriceDetail />
			<DailyChangeDetail />
			<OpenInterestDetail isLong />
			<OpenInterestDetail />
			<MarketSkew />
			<HourlyFundingDetail />
		</MarketDetailsContainer>
	)

	return <MainContainer mobile={mobileOrTablet}>{SelectedMarketDetailsView}</MainContainer>
})

const MarketPriceDetail = memo(() => {
	const { t } = useTranslation()

	const markPrice = useAppSelector(selectSkewAdjustedPriceInfo)
	const asset = useAppSelector(selectMarketAsset)
	const prices = useAppSelector(selectOffchainPricesInfo)

	const market = useMemo(() => {
		return MARKETS_LIST.find((market) => market.asset === asset) ?? DEFAULT_MARKET
	}, [asset])

	const originalAssetPrice = useMemo(() => {
		if (market?.originalAsset) {
			return wei(prices[market.originalAsset]?.price || 0)
		}
		return ZERO_WEI
	}, [prices, market?.originalAsset])

	return (
		<PriceContainer columnGap="5px">
			<StyledColoredPrice priceChange={markPrice?.change}>
				{markPrice
					? formatDollars(wei(markPrice.price), { suggestDecimalsForAsset: asset })
					: NO_VALUE}
			</StyledColoredPrice>
			{market?.originalAsset && (
				<MarketPriceTooltip
					id="market-price-tooltip"
					trigger={<QuestionMarkIcon />}
					content={
						<FlexDivCol rowGap="5px">
							<FlexDivCol>
								<Body className="heading">{t('futures.market.info.formula')}</Body>
								<Body as="span" mono>
									{market.transformFormula}
								</Body>
							</FlexDivCol>
							<FlexDivCol>
								<Body className="heading">{t('futures.market.info.underlying-price')}</Body>
								<Body as="span" mono>
									{originalAssetPrice
										? formatDollars(originalAssetPrice, {
												suggestDecimalsForAsset: market.originalAsset,
											})
										: NO_VALUE}
								</Body>
							</FlexDivCol>
						</FlexDivCol>
					}
				/>
			)}
		</PriceContainer>
	)
})

const IndexPriceDetail: FC<MarketDetailsProps> = memo(({ mobile }) => {
	const indexPrice = useAppSelector(selectMarketPriceInfo)
	const asset = useAppSelector(selectMarketAsset)

	return (
		<MarketDetail
			mobile={mobile}
			dataKey={MarketDataKey.indexPrice}
			value={
				indexPrice
					? formatDollars(wei(indexPrice.price), { suggestDecimalsForAsset: asset })
					: NO_VALUE
			}
		/>
	)
})

const DailyChangeDetail: FC<MarketDetailsProps> = memo(({ mobile }) => {
	const priceChanges = useAppSelector(select24hrPriceChanges)
	const marketAsset = useAppSelector(selectMarketAsset)

	const change = priceChanges[marketAsset] ?? 0

	return (
		<MarketDetail
			mobile={mobile}
			dataKey={MarketDataKey.dailyChange}
			value={change ? formatPercent(change, { maxDecimals: 2 }) : NO_VALUE}
			color={change === 0 ? 'white' : change > 0 ? 'green' : 'red'}
		/>
	)
})

const HourlyFundingDetail: FC<MarketDetailsProps> = memo(({ mobile }) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const marketInfo = useAppSelector(selectMarketInfo)
	const interestRate = useAppSelector(selectPerpsInterestRate)

	const fundingHours = useAppSelector(selectSelectedInputHours)

	const updateFundingRateHour = useCallback(
		(v: number) => {
			dispatch(setPreferences({ selectedInputHours: v }))
		},
		[dispatch]
	)

	const fundingValue = useMemo(() => {
		if (!marketInfo) return wei(0)
		const fundingRateHourly = wei(marketInfo.currentFundingRate)
		const fundingRate = fundingRateHourly.mul(wei(fundingHours))
		const interest = interestRate.hourly.mul(wei(fundingHours))
		return fundingRate.add(interest)
	}, [fundingHours, interestRate.hourly, marketInfo])

	return (
		<MarketDetail
			dataKey={t('futures.market.info.hourly-funding')}
			value={
				fundingValue ? (
					<MarketDetailValue
						color={
							fundingValue?.gt(ZERO_WEI) ? 'green' : fundingValue?.lt(ZERO_WEI) ? 'red' : undefined
						}
						value={formatPercent(fundingValue ?? ZERO_WEI, {
							suggestDecimals: true,
							maxDecimals: 6,
						})}
						mobile={mobile}
					/>
				) : (
					NO_VALUE
				)
			}
			mobile={mobile}
			extra={
				<HoursToggle>
					<RoundedSelect
						size="small"
						values={FUNDING_RATE_PERIODS}
						selectedValue={fundingHours}
						defaultLabel="1H"
						onChange={updateFundingRateHour}
					/>
				</HoursToggle>
			}
		/>
	)
})

const MarketSkew: FC<MarketDetailsProps> = memo(({ mobile }) => {
	const marketInfo = useAppSelector(selectMarketInfo)

	return (
		<MarketDetail
			mobile={mobile}
			dataKey={MarketDataKey.skew}
			value={
				<>
					<MarketDetailValue
						color="green"
						value={formatPercent(marketInfo ? marketInfo?.openInterest.longPct : 0, {
							minDecimals: 0,
						})}
						mobile={mobile}
					/>
					{'/'}
					<MarketDetailValue
						color="red"
						value={formatPercent(marketInfo ? marketInfo?.openInterest.shortPct : 0, {
							minDecimals: 0,
						})}
						mobile={mobile}
					/>
				</>
			}
		/>
	)
})

const OpenInterestDetail: FC<OpenInterestDetailProps> = memo(({ mobile, isLong }) => {
	const marketInfo = useAppSelector(selectMarketInfo)
	const oiCap = marketInfo?.marketLimitUsd
		? formatDollars(isLong ? marketInfo?.marketLimitUsd.long : marketInfo.marketLimitUsd.short, {
				truncateOver: 1e3,
			})
		: NO_VALUE
	const openInterestType = isLong ? 'longUSD' : 'shortUSD'
	const formattedUSD = marketInfo?.openInterest[openInterestType]
		? formatDollars(marketInfo?.openInterest[openInterestType], { truncateOver: 1e3 })
		: NO_VALUE

	const mobileValue = (
		<FlexDivCol>
			<div>{formattedUSD}</div>
			<Body mono size="small" color="secondary" weight="bold">
				{oiCap}
			</Body>
		</FlexDivCol>
	)

	const desktopValue = `${formattedUSD}/${oiCap}`

	const dataKey = `openInterest${isLong ? 'Long' : 'Short'}${
		mobile ? 'Mobile' : ''
	}` as keyof typeof MarketDataKey

	return (
		<MarketDetail
			mobile={mobile}
			dataKey={MarketDataKey[dataKey]}
			value={mobile ? mobileValue : desktopValue}
		/>
	)
})

const PriceContainer = styled(FlexDivRowCentered)`
	padding: 0 15px;
	border-right: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
	border-left: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
	${media.lessThan('md')`
		border-right: none;
		padding-left: 15px;
		margin: auto;
	`}
`
const MainContainer = styled.div<{ mobile?: boolean }>`
	display: grid;
	align-items: center;
	// height: ${MARKETS_DETAILS_HEIGHT_DESKTOP}px;
	height: 100%;
	overflow-y: visible;
	padding-right: 10px;

	${(props) =>
		props.mobile &&
		css`
			display: flex;
			flex-direction: column;
			height: auto;
			padding-right: 0px;
		`}
`

const MarketDetailsContainer = styled.div<{
	mobile?: boolean
	perpsProvider?: PerpsProvider
}>`
	flex: 1;
	scrollbar-width: none;
	display: flex;
	align-items: center;
	width: 100%;
	overflow-x: scroll;

	& > div {
		padding: 0 15px;
		border-right: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
	}

	& > div:first-child {
		padding: 0;
		border-right: none;
	}

	& > div:last-child {
		border-right: none;
	}

	${media.lessThan('md')`
		gap: 0px;
		& > div {
			margin-right: 0px;
			border-right: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
		}
	`}

	.heading, .value {
		white-space: nowrap;
	}

	${(props) => css`
		.heading {
			color: ${props.theme.colors.selectedTheme.text.label};
		}

		.value {
			color: ${props.theme.colors.selectedTheme.text.value};
		}

		.green {
			color: ${props.theme.colors.selectedTheme.green};
		}

		.red {
			color: ${props.theme.colors.selectedTheme.red};
		}

		.paused {
			color: ${props.theme.colors.selectedTheme.gray};
		}

		${
			props.mobile &&
			css`
			height: auto;
			width: 100%;
			padding: 0px;
			display: flex;
			flex-direction: column;
			justify-items: start;
			align-items: start;
			justify-content: start;
			margin: 0px;
			& > div {
				margin-right: 0px;
			}

			border-left: none;
			.heading {
				margin-bottom: 2px;
			}

			& > div {
				padding: ${props.perpsProvider === PerpsProvider.SNX_V2_OP ? 8 : 10}px 0px;
			}
		`
		}
	`}
`

const HoursToggle = styled.div`
	margin-left: 8px;
	margin-top: 12px;
	& > div > div {
		font-weight: 400;
	}
`

const StyledColoredPrice = styled(ColoredPrice)`
	font-size: 18px;
	font-weight: 700;
	font-feature-settings: 'tnum';
`

const StyledDialogOverlay = styled(DialogOverlay)`
	z-index: 10;
	background: rgba(0, 0, 0, 0.5);
`

const DropdownContainer = styled.div<{ showBanner: boolean; deviceType: string }>`
	position: absolute;
	z-index: 100;
	${(props) => {
		return props.deviceType === 'mobile'
			? css`
					top: calc(50px + ${props.showBanner ? BANNER_HEIGHT_MOBILE : 0}px);
					left: 0px;
			  `
			: css`
					top: calc(50px + ${props.showBanner ? BANNER_HEIGHT_DESKTOP : 0}px);
					left: 330px;
			  `
	}}
`

const OtherMarketDetailsContainer = styled(FlexDivRow)`
	overflow-x: scroll;
	border-top: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
	width: 100%;
	${media.lessThan('lg')`
		border: none;
	`}

	${media.lessThan('md')`
		border-top: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
		border-bottom: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
	`}

	& div:last-child {
		border-right: none;
	}
`

const MainMarketDetailsContainer = styled(FlexDivRowCentered)`
	width: 100%;
	justify-content: space-between;
	padding-right: 15px;
	${media.lessThan('lg')`
		border: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
		border-left: none;
		height: ${MARKETS_DETAILS_HEIGHT_DESKTOP}px;
	`}

	${media.lessThan('md')`
		border: none;
	`}
`

const MarketPriceTooltip = styled(Tooltip)`
	z-index: 1;
	max-width: 300px;
	cursor: pointer;
	${media.lessThan('lg')`
		border-right: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
		margin: auto;
	`}
`
const FullWidthFlexDivRowCentered = styled(FlexDivRowCentered)`
	width: 100%;
`

const MenuButton = styled(Button)`
	display: grid;
	place-items: center;
	height: 41px;
	width: 41px;
	padding: 0px;
	border: none;

	svg {
		path {
			color: ${(props) => props.theme.colors.selectedTheme.newTheme.tabs.position.inactive};
		}
	}
`

export default MarketDetails
